/**
 * Pure helpers behind the arrows on the replicas chart that move the
 * stretch shown to earlier or later times. The scaling reads only take a
 * number of hours back from now, so moving back means loading a longer
 * window and drawing the part being looked at. No DOM, so testable as is.
 *
 * `now` is a parameter everywhere, with the clock as its default, so the
 * tests pin it and the hook stays free of clock reads during render.
 * The hook takes its flags from viewAt, so they all share one read.
 */

import type { TimeRange } from '@/components/models/home/replicaZoom'
import { MAX_WINDOW_HOURS } from '@/components/models/home/scalingWindow'

export const HOUR_MS = 60 * 60 * 1000
/**
 * The gateway keeps 7 days of samples: how far back the chart can be moved.
 * The metrics page passes its own limit (30 days of rollups, TRA-1592).
 */
export const PAN_LIMIT_MS = MAX_WINDOW_HOURS * HOUR_MS

/**
 * Where the chart looks. `end` is the end of the picked window; null while
 * it ends at now and follows it. `zoom` is a dragged stretch in absolute
 * times, always inside that window.
 */
export interface PanState {
  end: number | null
  zoom: TimeRange | null
}

export const AT_NOW: PanState = { end: null, zoom: null }

/** The window ending at `end`, not zoomed; at now when `end` is null. */
export function atEnd(end: number | null): PanState {
  return { end, zoom: null }
}

export type PanDirection = 'earlier' | 'later'

/** The stretch on screen: the zoom, or the picked window ending at `end` or now. */
export function visibleSpan(
  state: PanState,
  windowHours: number,
  now: number = Date.now(),
): TimeRange {
  if (state.zoom !== null) return state.zoom
  const end = state.end ?? now
  return { start: end - windowHours * HOUR_MS, end }
}

/** The stretch to draw, or null for the whole loaded window: at now and not zoomed. */
export function shownRange(state: PanState, windowHours: number): TimeRange | null {
  if (state.zoom !== null) return state.zoom
  if (state.end === null) return null
  return { start: state.end - windowHours * HOUR_MS, end: state.end }
}

/**
 * The span moved by `stepMs`, half its width unless the caller says
 * otherwise (the metrics page steps in whole buckets, TRA-1592), held
 * inside the `limitMs` up to now: a step that would pass an edge stops
 * at it.
 */
export function stepSpan(
  span: TimeRange,
  direction: PanDirection,
  now: number,
  limitMs: number = PAN_LIMIT_MS,
  stepMs: number = (span.end - span.start) / 2,
): TimeRange {
  const width = span.end - span.start
  const moved = direction === 'earlier' ? span.start - stepMs : span.start + stepMs
  const start = Math.min(Math.max(moved, now - limitMs), now - width)
  return { start, end: start + width }
}

/** Whether an arrow has anywhere to go: earlier until the limit, later until now. */
export function canStep(
  span: TimeRange,
  direction: PanDirection,
  now: number = Date.now(),
  limitMs: number = PAN_LIMIT_MS,
): boolean {
  return direction === 'earlier' ? span.start > now - limitMs : span.end < now
}

/**
 * The state after an arrow press. Zoomed, the zoom moves and the window
 * ends where it ends, so Reset zoom shows the window around it. Not
 * zoomed, the window moves. A stretch that reaches now follows now again.
 */
export function stepped(
  state: PanState,
  windowHours: number,
  direction: PanDirection,
  now: number,
  limitMs: number = PAN_LIMIT_MS,
  stepMs?: number,
): PanState {
  const next = stepSpan(visibleSpan(state, windowHours, now), direction, now, limitMs, stepMs)
  const end = next.end >= now ? null : next.end
  return { end, zoom: state.zoom === null ? null : next }
}

/** What the chart needs loaded and where its arrows can go, from one clock read. */
export interface ViewAt {
  loadHours: number
  canEarlier: boolean
  canLater: boolean
}

/**
 * The flags for one render, all against the same `now`. Read apart, a
 * later read sits past the span's end, so a 7-day window at now shows
 * both arrows enabled while a press moves nothing.
 */
export function viewAt(state: PanState, windowHours: number, now: number = Date.now()): ViewAt {
  const span = visibleSpan(state, windowHours, now)
  return {
    loadHours: hoursToLoad(state, windowHours, now),
    canEarlier: canStep(span, 'earlier', now),
    canLater: canStep(span, 'later', now),
  }
}

/**
 * The whole hours back from now the reads must cover so the window is
 * loaded: the window itself at now, more when moved back, rounded up and
 * never past `maxHours`, the most the read keeps (7 days of samples; the
 * engine read keeps 30).
 */
export function hoursToLoad(
  state: PanState,
  windowHours: number,
  now: number = Date.now(),
  maxHours: number = MAX_WINDOW_HOURS,
): number {
  if (state.end === null) return windowHours
  const start = state.end - windowHours * HOUR_MS
  return Math.min(maxHours, Math.ceil((now - start) / HOUR_MS))
}
